import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface ProgressReportEntry {
  id: string;
  report_type: string;
  recipient_id: string | null;
  report_data: Record<string, never> | null;
  ai_insights: string | null;
  delivery_status: string | null;
  delivery_channels: string[] | null;
  generated_at: string;
  sent_at: string | null;
}

export interface ProgressReportListPayload {
  reports: ProgressReportEntry[];
  total: number;
}

// ── Server Functions ──────────────────────────────────────
export const getProgressReports = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      page: z.number().int().min(1).default(1),
      limit: z.number().int().min(1).max(100).default(20),
      reportType: z.enum(["daily", "weekly", "monthly"]).optional(),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const offset = (data.page - 1) * data.limit;

    let query = client
      .from("progress_report_history")
      .select("id, report_type, recipient_id, report_data, ai_insights, delivery_status, delivery_channels, generated_at, sent_at", { count: "exact" })
      .order("generated_at", { ascending: false })
      .range(offset, offset + data.limit - 1);

    if (data.reportType) {
      query = query.eq("report_type", data.reportType);
    }

    const { data: reports, error, count } = await query;

    if (error) return { data: null, error: error.message };

    const entries: ProgressReportEntry[] = (reports ?? []).map((r) => ({
      id: String(r.id),
      report_type: String(r.report_type),
      recipient_id: r.recipient_id ? String(r.recipient_id) : null,
      report_data: (r.report_data as Record<string, never>) ?? null,
      ai_insights: r.ai_insights ? String(r.ai_insights) : null,
      delivery_status: r.delivery_status ? String(r.delivery_status) : null,
      delivery_channels: Array.isArray(r.delivery_channels) ? r.delivery_channels.map(String) : null,
      generated_at: String(r.generated_at),
      sent_at: r.sent_at ? String(r.sent_at) : null,
    }));

    return { data: { reports: entries, total: count ?? 0 }, error: null };
  });
